import { gql } from "@apollo/client";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ListItemText,
  Menu,
  MenuItem,
} from "@mui/material";
import { useRef, useState } from "react";

import Help from "~/components/common/Help";
import { ApiKeyPurpose, CreateApiKeyMutation, useCreateApiKeyMutation, useCreateWebhookApiKeyMutation } from "~/graphql/__generated__/graphql";
import KeyCreatedDialog from "./KeyCreatedDialog";

const CreateApiKey = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [fipsComplianceDialogOpen, setFipsComplianceDialogOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreateApiKeyMutation["createApiKey"] | null>(null);
  const popoverEventElement = useRef<null | HTMLAnchorElement>(null);

  const [createApiKey, { loading: createApiKeyLoading }] = useCreateApiKeyMutation({
    refetchQueries: ["ApiKeys"],
    fetchPolicy: 'no-cache', // Do not cache response since it includes secrets
  });
  const [createWebhookKey, { loading: createWebhookKeyLoading }] = useCreateWebhookApiKeyMutation({
    refetchQueries: ["ApiKeys"],
    fetchPolicy: 'no-cache', // Do not cache response since it includes secrets
  });

  const createKey = async (keyPurpose: ApiKeyPurpose, requireFipsCompliance: boolean | null = null) => {
    setMenuOpen(false);
    const result = await createApiKey({
      variables: {
        expiresAt: null,
        keyPurpose,
        requireFipsCompliance,
      }
    });
    setFipsComplianceDialogOpen(false);
    setCreatedKey(result.data?.createApiKey ?? null);
  }

  return <>
    <Button
      size="small"
      startIcon={<AddCircleOutlineIcon fontSize="small" />}
      loading={createApiKeyLoading || createWebhookKeyLoading}
      ref={popoverEventElement as any}
      onClick={() => setMenuOpen(true)}
    >
      Create New API Key
    </Button>

    <Menu
      anchorEl={popoverEventElement.current}
      open={menuOpen}
      onClose={() => setMenuOpen(false)}
      slotProps={{ list: {
        'aria-labelledby': 'basic-button',
      }}}
    >
      <MenuItem onClick={() => createKey(ApiKeyPurpose.Endpoint)}>
        <ListItemText>
          Endpoint Key
          <Help text="Endpoint API keys are used to make calls into Medarrive. The secret key is available only once, during key creation, while Medarrive stores the public key to authenticate incoming requests."/>
        </ListItemText>
      </MenuItem>

      <MenuItem onClick={async () => {
        setMenuOpen(false);
        setFipsComplianceDialogOpen(true);
      }}>
        <ListItemText>
          SFTP Key
          <Help text="SFTP keys are used to upload files to Medarrive via SFTP, such as HL7 messages. The secret key is available only once, during key creation, while Medarrive stores the public key to authenticate SFTP logins. The key ID is your SFTP username."/>
        </ListItemText>
      </MenuItem>

      <MenuItem onClick={() => {
        setMenuOpen(false);
        // Webhook keys only return the public key; we store the private one internally.
        // Thus, no dialog needed to give them a chance to save anything..
        createWebhookKey();
      }}>
        <ListItemText>
          Webhook Key
          <Help text="Webhook API keys are used to sign webhook calls coming from medarrive. You can download the public key at any time, while Medarrive stores the private key securely and signs calls it makes to your system." placement="bottom"/>
        </ListItemText>
      </MenuItem>
    </Menu>

    {fipsComplianceDialogOpen && <Dialog open={true} onClose={() => setFipsComplianceDialogOpen(false)}>
      <DialogTitle>Create SFTP Key</DialogTitle>
      <DialogContent>We support either RSA (FIPS compliant) or ed25519 (faster, more secure, industry standard) keys. Only choose the former if your organization requires FIPS compliance.</DialogContent>
      <DialogActions>
        <Button onClick={() => setFipsComplianceDialogOpen(false)}>Cancel</Button>
        <Button
          loading={createApiKeyLoading}
          variant="outlined"
          onClick={() => createKey(ApiKeyPurpose.Sftp, true)}
        >FIPS-compliant Key</Button>
        <Button
          variant="contained"
          loading={createApiKeyLoading}
          onClick={() => createKey(ApiKeyPurpose.Sftp, false)}
        >Create Key</Button>
      </DialogActions>
    </Dialog>}
    {createdKey && <KeyCreatedDialog createdKey={createdKey} onClose={() => setCreatedKey(null)} />}
  </>
}

export default CreateApiKey;

gql`
  mutation CreateApiKey(
    $expiresAt: String
    $keyPurpose: ApiKeyPurpose!
    $requireFipsCompliance: Boolean
  )
  @permission(requires: manageApiKeys) {
    createApiKey(
      expiresAt: $expiresAt
      keyPurpose: $keyPurpose
      requireFipsCompliance: $requireFipsCompliance
    ) {
      apiKey {
        id
        apiKey
        keyPurpose
        expiresAt
        createdAt
        updatedAt
      }
      privatePem
      privateDer
    }
  }
`;

gql`
  mutation CreateWebhookApiKey($expiresAt: String)
  @permission(requires: manageApiKeys) {
    createWebhookApiKey(expiresAt: $expiresAt) {
      apiKey {
        id
        apiKey
        expiresAt
        createdAt
        updatedAt
      }
      publicPem
    }
  }
`;
